import React, { useState, useRef, useEffect } from "react";

export function AdminLogin({ onLoginSuccess }) {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [step, setStep] = useState(1);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  const handleChange = (element, index) => {
    const value = element.value.replace(/\D/, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const requestOtp = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch("https://api.sysfileinfo.online/auth/request-otp", { method: "POST" });
      if (res.ok) {
        setStep(2);
        setMsg("OTP sent to your email ✉️");
      } else {
        setMsg("Failed to send OTP ❌");
      }
    } catch {
      setMsg("Server not reachable ⚠️");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    const otpCode = otp.join("");
    if (otpCode.length < 6) return;
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch("https://api.sysfileinfo.online/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp: otpCode }),
      });
      const text = await res.text();
      if (res.ok) {
        localStorage.setItem("admin-auth", "true");
        setMsg("✅ Login successful! Redirecting...");
        setTimeout(() => onLoginSuccess(), 500);
      } else {
        setMsg(text || "Invalid OTP ❌");
      }
    } catch {
      setMsg("Server not reachable ⚠️");
    }
    setLoading(false);
  };

  const [bubbles, setBubbles] = useState([]);
  useEffect(() => {
    const bubbleCount = 25;
    const newBubbles = Array.from({ length: bubbleCount }, (_, i) => ({
      left: Math.random() * 100 + "%",
      size: Math.random() * 20 + 10 + "px",
      duration: Math.random() * 15 + 10 + "s",
      delay: Math.random() * 5 + "s",
    }));
    setBubbles(newBubbles);
  }, []);

  return (
    <div style={styles.page}>
      {bubbles.map((b, i) => (
        <div
          key={i}
          className="bubble"
          style={{
            ...styles.bubble,
            left: b.left,
            width: b.size,
            height: b.size,
            animationDuration: b.duration,
            animationDelay: b.delay,
          }}
        />
      ))}

      {/* Transparent login card */}
      <div style={styles.card}>
        <h1 style={styles.title}>🔐 Admin Login</h1>

        {step === 1 && (
          <button style={styles.button} onClick={requestOtp} disabled={loading}>
            {loading ? "Sending OTP..." : "Send OTP"}
          </button>
        )}

        {step === 2 && (
          <>
            <div style={styles.otpContainer}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleChange(e.target, i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  ref={(el) => (inputRefs.current[i] = el)}
                  style={styles.otpInput}
                />
              ))}
            </div>
            <button
              style={{ ...styles.button, marginTop: "20px" }}
              onClick={verifyOtp}
              disabled={loading || otp.join("").length < 6}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </>
        )}

        {msg && <p style={styles.msg}>{msg}</p>}
      </div>

      <style>{`
        .bubble {
          position: absolute;
          bottom: -50px;
          background-color: rgba(255,255,255,0.2);
          border-radius: 50%;
          animation-name: float;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          transition: transform 0.3s, opacity 0.3s;
        }
        .bubble:hover {
          transform: scale(1.5) translateY(-20px);
          opacity: 0.8;
        }
        @keyframes float {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.4; }
          50% { opacity: 0.6; }
          100% { transform: translateY(-120vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    overflow: "hidden",
  },
  card: {
    zIndex: 1,
    background: "rgba(255, 255, 255, 0.15)", // transparent card
    backdropFilter: "blur(2px)",           // glass effect
    padding: "50px 40px",
    borderRadius: "20px",
    boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
    textAlign: "center",
    width: "280px",
    height: "400px",
  },
  title: {
    marginBottom: "40px",
    color: "#fff",  // text white for contrast
    fontSize: "28px",
    fontWeight: "600",
  },
  otpContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "6px",
  },
  otpInput: {
    width: "35px",
    height: "40px",
    fontSize: "24px",
    textAlign: "center",
    borderRadius: "10px",
    border: "2px solid rgba(255,255,255,0.5)",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    transition: "0.3s",
    outline: "none",
  },
  button: {
    width: "100%",
    padding: "14px 18px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(90deg, #6C63FF, #8e54e9)",
    color: "#fff",
    fontSize: "18px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "0.3s",
    boxShadow: "0 5px 15px rgba(108, 99, 255, 0.4)",
  },
  msg: {
    marginTop: "20px",
    fontSize: "15px",
    color: "#fff",
    transition: "opacity 0.4s ease",
  },
};
